import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/format';
import { centsToNumber } from '@/lib/money';
import {
  customLine,
  draftFromCatalogItem,
  draftFromLine,
  labourLine,
  lineCents,
  moveLine,
  toSaveLine,
  validateLine,
  type DraftLine,
} from '@/lib/estimateLines';
import {
  EstimateInvalidError,
  EstimateRefusedError,
  useEstimatePreview,
  useSaveEstimateDraft,
  type Estimate,
  type SaveDraft,
} from '@/routes/estimates/useEstimates';
import { CatalogSearch } from './CatalogSearch';
import { EstimateNotesEditor } from './EstimateNotes';
import { EstimateLineRow, LINE_GRID } from './EstimateLineRow';

/** How long after the last keystroke a draft saves itself. */
export const AUTOSAVE_DELAY_MS = 800;

/**
 * The draft editor (M2-06): price-book search, custom and labour lines,
 * reordering, discounts, live totals, the two notes (M2-07) — and it saves
 * itself.
 *
 * <b>The notes ride with the lines, always.</b> The API replaces both notes
 * with every save, so a body without them clears them. The snapshot the
 * autosave compares and sends is lines AND notes, and so is the one flushed
 * on unmount — there is no save here that can leave a note out.
 *
 * <b>The lines on screen are the truth; the server's copy follows them.</b>
 * Every edit is local and instant. Once the lines have been still for
 * {@link AUTOSAVE_DELAY_MS} and every one of them is valid, the whole set is
 * PUT (the API replaces lines wholesale). The response is NOT poured back into
 * the editor — line ids change on every save, and the person may have typed
 * again while it was in flight — so the editor keeps its own row keys and
 * compares what it last saved with what it has now.
 *
 * <b>Totals come from two places, on purpose.</b> A line's total and the
 * subtotal are worked out here as the person types (`lib/money.ts`, the API's
 * rounding restated); tax and the grand total come from the preview endpoint,
 * because tax depends on the property's jurisdiction and rate, which the
 * browser cannot see. The preview is the send's own arithmetic.
 */
export function EstimateBuilder({
  estimate,
  hourlyRate,
  onSettledChange,
}: {
  estimate: Estimate;
  /** The job lead's billable rate, when the caller may see it (Owner/Admin); null otherwise. */
  hourlyRate: number | null;
  /**
   * Whether the server holds exactly what is on screen: valid, saved, nothing
   * in flight. ⚠️ Send waits on this — sending prices the SAVED lines, so a
   * send half a second after a keystroke would freeze the version before it.
   */
  onSettledChange?: (settled: boolean) => void;
}) {
  const { t } = useTranslation();
  const [lines, setLines] = useState<DraftLine[]>(() => estimate.lines.map(draftFromLine));
  const [customerMessage, setCustomerMessage] = useState(estimate.customerMessage ?? '');
  const [internalNotes, setInternalNotes] = useState(estimate.internalNotes ?? '');
  // Lines the person has left at least once: only those show their errors, so
  // a line being typed is not shouted at before it is finished.
  const [visited, setVisited] = useState<ReadonlySet<string>>(
    () => new Set(lines.map((l) => l.key)),
  );
  const [focusRequest, setFocusRequest] = useState<{ key: string; field: string } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const valid = lines.every((line) => validateLine(line) === null);
  const payload = valid ? lines.map(toSaveLine) : null;
  // What a save sends — ⚠️ both notes every time, never the lines alone.
  const snapshot =
    payload === null
      ? null
      : JSON.stringify({ lines: payload, customerMessage, internalNotes } satisfies SaveDraft);

  const save = useSaveEstimateDraft(estimate.id);
  const [lastSaved, setLastSaved] = useState(() =>
    JSON.stringify({
      lines: lines.map(toSaveLine),
      customerMessage,
      internalNotes,
    } satisfies SaveDraft),
  );
  const [failedSnapshot, setFailedSnapshot] = useState<string | null>(null);
  const refusal = save.error instanceof EstimateRefusedError ? save.error.message : null;
  // A 400 is the server refusing THESE lines — its sentence is the fix, and
  // retrying the same lines can never work (found in review).
  const rejection =
    save.error instanceof EstimateInvalidError && failedSnapshot === snapshot
      ? save.error.message
      : null;
  // The same 400, per field, for the notes' own boxes (`CustomerMessage`, `InternalNotes`).
  const rejectedFields =
    save.error instanceof EstimateInvalidError && failedSnapshot === snapshot
      ? save.error.errors
      : NO_ERRORS;

  function saveNow(json: string) {
    save.mutate(JSON.parse(json) as SaveDraft, {
      onSuccess: () => {
        setLastSaved(json);
        setFailedSnapshot(null);
      },
      onError: () => setFailedSnapshot(json),
    });
  }

  // Autosave: when the lines are valid, differ from what was last saved, have
  // been still for the delay, and nothing is in flight. A failed save is not
  // retried on its own for the SAME lines — the next edit, or Try again, does.
  useEffect(() => {
    if (snapshot === null || snapshot === lastSaved || save.isPending || refusal !== null) return;
    if (snapshot === failedSnapshot) return;
    const timer = setTimeout(() => saveNow(snapshot), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // saveNow is recreated every render; the snapshot is what matters.
  }, [snapshot, lastSaved, save.isPending, failedSnapshot, refusal]);

  // ⚠️ Leaving the builder INSIDE the app — a link, Back — unmounts it, and the
  // autosave timer dies with it: the last edit was silently lost (found in
  // review). The latest unsaved snapshot is kept here and sent on unmount;
  // the mutation outlives the component. Not after a refusal or for lines
  // that already failed — those would only fail again.
  const unsaved = useRef<string | null>(null);
  const saveLines = useRef(save.mutate);
  useEffect(() => {
    saveLines.current = save.mutate;
    unsaved.current =
      snapshot !== null && snapshot !== lastSaved && snapshot !== failedSnapshot && refusal === null
        ? snapshot
        : null;
  });
  useEffect(
    () => () => {
      if (unsaved.current !== null) saveLines.current(JSON.parse(unsaved.current) as SaveDraft);
    },
    [],
  );

  // Leaving with a change not yet saved: say so before the tab closes.
  const dirty = snapshot !== lastSaved;
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const preview = useEstimatePreview(estimate.id, payload, refusal === null);

  const settled = valid && !dirty && !save.isPending && refusal === null;
  useEffect(() => {
    onSettledChange?.(settled);
  }, [settled, onSettledChange]);

  useEffect(() => {
    if (!focusRequest) return;
    listRef.current
      ?.querySelector<HTMLElement>(
        `[data-line-key="${focusRequest.key}"] [aria-label="${focusRequest.field}"]`,
      )
      ?.focus();
    setFocusRequest(null);
  }, [focusRequest]);

  function update(key: string, next: DraftLine) {
    setLines((current) => current.map((line) => (line.key === key ? next : line)));
  }

  function add(line: DraftLine, focusField: string | null) {
    setLines((current) => [...current, line]);
    if (focusField) setFocusRequest({ key: line.key, field: focusField });
  }

  function move(index: number, by: -1 | 1) {
    setLines((current) => moveLine(current, index, by));
  }

  const totals = lines.map(lineCents);
  const subtotal = valid ? totals.reduce((sum, c) => sum + c!.total, 0n) : null;
  const discounts = valid ? totals.reduce((sum, c) => sum + c!.discount, 0n) : 0n;

  const status = refusal
    ? t('estimates.builder.saveRefused', { reason: refusal })
    : save.isPending
      ? t('estimates.builder.saving')
      : !valid
        ? t('estimates.builder.invalid')
        : rejection
          ? t('estimates.builder.saveRejected', { reason: rejection })
          : failedSnapshot !== null && failedSnapshot === snapshot
            ? t('estimates.builder.saveFailed')
            : dirty
              ? t('estimates.builder.unsaved')
              : t('estimates.builder.saved');

  return (
    <section aria-labelledby="estimate-lines-heading" className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="estimate-lines-heading" className="text-base font-semibold">
          {t('estimates.builder.heading')}
        </h2>
        <p
          role="status"
          aria-live="polite"
          className="text-sm text-[var(--color-muted-foreground)]"
          data-testid="save-status"
        >
          {status}
          {failedSnapshot !== null &&
          failedSnapshot === snapshot &&
          !save.isPending &&
          !rejection ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-2"
              onClick={() => saveNow(snapshot!)}
            >
              {t('estimates.builder.retry')}
            </Button>
          ) : null}
        </p>
      </div>

      <CatalogSearch ref={searchRef} onAdd={(item) => add(draftFromCatalogItem(item), null)} />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => add(customLine(), t('estimates.line.description'))}
        >
          {t('estimates.builder.addCustom')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            add(
              labourLine(t('estimates.builder.labourDescription'), hourlyRate),
              t('estimates.line.quantityHours'),
            )
          }
        >
          {t('estimates.builder.addLabour')}
        </Button>
      </div>

      <div ref={listRef} className="space-y-3" data-testid="estimate-lines">
        {lines.length > 0 ? (
          // Column headings for the eye, from md up; each field is named on its own.
          <div
            aria-hidden="true"
            className={`hidden gap-2 text-xs font-medium text-[var(--color-muted-foreground)] md:grid ${LINE_GRID}`}
          >
            <span>{t('estimates.line.description')}</span>
            <span className="text-right">{t('estimates.line.quantity')}</span>
            <span className="text-right">{t('estimates.line.unitPrice')}</span>
            <span>{t('estimates.line.taxCategory')}</span>
            <span>{t('estimates.line.discount')}</span>
            <span className="text-right">{t('estimates.line.total')}</span>
            <span />
          </div>
        ) : null}
        {lines.length === 0 ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('estimates.builder.empty')}
          </p>
        ) : (
          lines.map((line, index) => (
            <div
              key={line.key}
              data-line-key={line.key}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setVisited((current) => new Set(current).add(line.key));
                }
              }}
            >
              <EstimateLineRow
                line={line}
                index={index}
                count={lines.length}
                showErrors={visited.has(line.key)}
                onChange={(next) => update(line.key, next)}
                onMove={(by) => move(index, by)}
                onRemove={() => {
                  setLines((current) => current.filter((l) => l.key !== line.key));
                  searchRef.current?.focus();
                }}
              />
            </div>
          ))
        )}
        {lines.length > 1 ? (
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {t('estimates.builder.shortcuts')}
          </p>
        ) : null}
      </div>

      <dl
        className="ml-auto grid max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm"
        data-testid="estimate-totals"
      >
        <dt>{t('estimates.totals.subtotal')}</dt>
        <dd className="text-right tabular-nums" data-testid="subtotal">
          {subtotal === null ? '—' : formatCurrency(centsToNumber(subtotal))}
        </dd>
        {discounts > 0n ? (
          <>
            <dt className="text-[var(--color-muted-foreground)]">
              {t('estimates.totals.discounts')}
            </dt>
            <dd className="text-right tabular-nums text-[var(--color-muted-foreground)]">
              {formatCurrency(-centsToNumber(discounts))}
            </dd>
          </>
        ) : null}
        <dt>{t('estimates.totals.tax')}</dt>
        <dd className="text-right tabular-nums" data-testid="tax">
          {!valid
            ? '—'
            : preview.current && preview.data!.tax !== null
              ? formatCurrency(Number(preview.data!.tax))
              : preview.current || preview.isError
                ? '—'
                : t('estimates.totals.updating')}
        </dd>
        <dt className="font-semibold">{t('estimates.totals.total')}</dt>
        <dd className="text-right font-semibold tabular-nums" data-testid="total">
          {!valid
            ? '—'
            : preview.current && preview.data!.total !== null
              ? formatCurrency(Number(preview.data!.total))
              : preview.current || preview.isError
                ? '—'
                : t('estimates.totals.updating')}
        </dd>
      </dl>
      <p className="text-right text-xs text-[var(--color-muted-foreground)]" data-testid="tax-note">
        {!valid
          ? t('estimates.totals.taxPending')
          : preview.error instanceof EstimateInvalidError
            ? t('estimates.totals.linesRefused', { reason: preview.error.message })
            : preview.isError
              ? t('estimates.totals.previewFailed')
              : preview.current && preview.data!.taxProblem
                ? t('estimates.totals.taxUnavailable', { reason: preview.data!.taxProblem })
                : t('estimates.totals.draftNote')}
      </p>

      <EstimateNotesEditor
        customerMessage={customerMessage}
        internalNotes={internalNotes}
        onCustomerMessageChange={setCustomerMessage}
        onInternalNotesChange={setInternalNotes}
        errors={rejectedFields}
      />
    </section>
  );
}

const NO_ERRORS: Record<string, string[]> = {};
